import { Button, Grid } from '@mui/material';
import Add from '@mui/icons-material/Add';
import React, { ChangeEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { UserContext } from 'components/UserGuard';
import useRequest from 'hooks/useRequest';
import Alert from '@mui/material/Alert';
import { FilterConfig } from 'store/types/Table';
import { defaultGridContainerProps, defaultGridItemProps } from 'util/Layout';
import SearchInput from 'components/shared/SearchInput';
import Spinner from 'components/shared/Spinner';
import ApplicationsService from 'services/api/ApplicationsService';
import routes from 'store/configs/Routes';
import Application from 'store/types/Application';
import ApplicationType from 'store/types/ApplicationType';
import CustomerType from 'store/enums/CustomerType';
import { getHashRouteUrl } from 'util/Route';
import { getUserRoles } from 'util/User';
import UserApplicationsTable from 'components/applications/UserApplicationsTable';
import { ParametersContext } from 'components/ParametersGuard';
import { getCategoryList, getFilteredAppTypes, getFormattedApplicationStatus } from 'util/Application';

import commonStyles from 'styles/common.module.scss';
import styles from './CompanyApplicationsPage.module.scss';

interface CompanyApplicationsPageProps {
  companyId: string;
}

const CompanyApplicationsPage: React.FunctionComponent<CompanyApplicationsPageProps> = ({ companyId }) => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [filter, setFilter] = useState<FilterConfig<Application>>({ search: '' });

  const {
    applications: { companyApplicationsTableHeader },
  } = useContext(ParametersContext);

  const applicationsRequest = useMemo(
    () => (companyId ? () => ApplicationsService.getUserApplications(companyId) : undefined),
    [companyId]
  );
  const applicationTypesRequest = useMemo(
    () => (companyId ? () => ApplicationsService.getApplicationTypes(companyId) : undefined),
    [companyId]
  );

  const { data, loading, error, refetch } = useRequest<Application[]>(applicationsRequest);

  const {
    data: appTypeData,
    loading: appTypeLoading,
    error: appTypeError,
  } = useRequest<ApplicationType[]>(applicationTypesRequest);

  const { functions } = useContext(UserContext);
  const userRoles: string[] = useMemo(() => getUserRoles(functions), [functions]);

  const filteredAppTypes: ApplicationType[] = useMemo(
    () => getFilteredAppTypes(appTypeData || [], userRoles, CustomerType.Company),
    [appTypeData, userRoles]
  );
  const categoryList: string[] = useMemo(() => getCategoryList(filteredAppTypes), [filteredAppTypes]);

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setFilter((prevState) => ({ ...prevState, search: e.target.value }));
  }, []);

  useEffect(() => {
    if (data?.length) {
      setApplications(
        data.map((application) => ({ ...application, status: getFormattedApplicationStatus(application.status) }))
      );
    }
  }, [data]);

  useEffect(() => {
    if (appTypeData?.length) {
      setCategories(categoryList);
    }
  }, [appTypeData, categoryList, data]);

  return error || appTypeError ? (
    <Alert severity={'error'} className={commonStyles.alert}>
      {error || appTypeError}
    </Alert>
  ) : (
    <Grid {...defaultGridContainerProps} justifyContent={'space-between'}>
      <Grid {...defaultGridItemProps} sm={true}>
        {appTypeLoading ? (
          <Spinner loading={appTypeLoading} transparent={false} />
        ) : (
          categories.map((category: string) => (
            <Button
              key={category}
              color={'secondary'}
              variant={'contained'}
              className={styles.categoryButtons}
              startIcon={<Add />}
              href={getHashRouteUrl(`${routes.createCompanyApplication}/${companyId}?category=${category}`)}
            >
              {category}
            </Button>
          ))
        )}
      </Grid>
      <Grid {...defaultGridItemProps} md={'auto'}>
        <SearchInput value={filter.search} onChange={handleSearchChange} loading={loading} />
      </Grid>
      <Grid {...defaultGridItemProps}>
        <h2 className={styles.tableTitle}>{'Compliance & Change Requests'}</h2>
        {companyApplicationsTableHeader && <p dangerouslySetInnerHTML={{ __html: companyApplicationsTableHeader }} />}
        <UserApplicationsTable
          data={applications}
          loading={loading}
          filter={filter}
          showApplicant={true}
          refetch={refetch}
        />
      </Grid>
    </Grid>
  );
};
export default CompanyApplicationsPage;
